import { useState, useEffect } from 'react';
import { Box, Typography, Paper } from '@mui/material';
// import imgEventBannerForPricingPage16 from "figma:asset/d6d7d4a97ce91e86d55556daa75d1fd4df16d111.png";

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

function CountdownBox({ value, label }: { value: number; label: string }) {
  return (
    <Paper
      elevation={0}
      sx={{
        bgcolor: 'white',
        borderRadius: '3.489px',
        px: '3.489px',
        py: '1.745px',
        width: '33.146px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '1.745px',
      }}
    >
      <Typography
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontWeight: 800,
          fontSize: '13.956px',
          color: 'black',
          height: '13.084px',
          lineHeight: 1,
          textAlign: 'center',
        }}
      >
        {String(value).padStart(2, '0')}
      </Typography>
      <Typography
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontWeight: 500,
          fontSize: '6.978px',
          color: 'black',
          textTransform: 'uppercase',
          textAlign: 'center',
        }}
      >
        {label}
      </Typography>
    </Paper>
  );
}

function Countdown({ targetDate }: { targetDate: Date }) {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  useEffect(() => {
    const calculateTimeLeft = (): TimeLeft => {
      const difference = targetDate.getTime() - new Date().getTime();

      if (difference > 0) {
        return {
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60),
        };
      }

      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    };

    // Initial calculation
    setTimeLeft(calculateTimeLeft());

    // Update every second
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  return (
    <Box
      sx={{
        display: 'flex',
        gap: '6.978px',
        alignItems: 'center',
      }}
    >
      <CountdownBox value={timeLeft.days} label="Days" />
      <CountdownBox value={timeLeft.hours} label="Hours" />
      <CountdownBox value={timeLeft.minutes} label="Min" />
    </Box>
  );
}

export default function EventBanner() {
  // Set target date to Dec 17, 2025, 11 AM PST (UTC-8)
  const targetDate = new Date('2025-12-17T11:00:00-08:00');

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        minHeight: '60px',
        borderRadius: '12px',
        overflow: 'hidden',
        background: 'linear-gradient(98deg, #2F0128 -8.21%, #0A1A4C 97.4%), linear-gradient(121deg, #E9DEFC 3.2%, #F5E8EA 72.01%)',
        marginBottom: '15px',
      }}
    >
      {/* Background Image Layer */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          borderRadius: '12px',
        }}
      >
        <Box
          component="img"
          // src={imgEventBannerForPricingPage16}
          alt=""
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: '12px',
          }}
        />
      </Box>

      {/* Content Layer */}
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: { xs: '8px', sm: '12px' },
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
          flexWrap: { xs: 'wrap', sm: 'wrap', md: 'wrap' },
        }}
      >
        <Countdown targetDate={targetDate} />

        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 500,
            fontSize: { xs: '14px', sm: '14px' },
            color: 'white',
            whiteSpace: { xs: 'normal', md: 'nowrap' },
            textAlign: { xs: 'center', md: 'left' },
            '& .event-name': {
              fontWeight: 700,
              color: '#fc5a40',
            },
            '& .bold-text': {
              fontWeight: 700,
            },
          }}
        >
          <span className="event-name">Casey Zeman Live</span>
          <span>{' • Dec 17 • 11 AM PST — '}</span>
          <span className="bold-text">Create & Scale</span>
          <span>{' High-Converting Webinars Into 2026. '}</span>
        </Typography>

        <Typography
          component="a"
          href="https://get.easywebinar.com/webinar-secret?utm_source=banner&utm_medium=website&utm_campaign=WS17DEC"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 500,
            fontSize: { xs: '14px', sm: '14px' },
            color: '#fbf662',
            whiteSpace: { xs: 'normal', md: 'nowrap' },
            textAlign: { xs: 'center', md: 'left' },
            cursor: 'pointer',
            textDecoration: 'none',
            transition: 'opacity 0.3s ease',
            '&:hover': {
              opacity: 0.8,
            },
          }}
        >
          Register Now & Join the Year-End Offer Waitlist! →
        </Typography>
      </Box>
    </Box>
  );
}
